/**
 * Push notifikácie glue pre natívnu wrapper appku — prihlási zariadenie na push
 * (OS prompt) a registračné tokeny odovzdáva backendu. Mimo appky no-op.
 *
 * KANONICKÝ ZDROJ: r-flutter-wrapper/docs/web/app-push.js — verziované spolu
 * s bridge kontraktom (docs/bridge-contract.md). Kópie v eshopoch lokálne
 * neupravovať; zmeny robiť tu a rozkopírovať.
 *
 * Ako to funguje:
 *  - Relay tokenov: počúva event `PUSH_TOKEN_READY` (subscribe aj každá rotácia)
 *    a odovzdá `{token, provider}` do sinku (backend). Dedup v rámci page loadu.
 *  - Auto-prompt (default zap., vypnuteľné `window.__appPushConfig.autoPrompt = false`):
 *    prompt sa NEvyvolá hneď po APP_READY, ale až keď je consent dorešený —
 *    na iOS tak vznikne poradie ATT prompt → reload → notifikačný prompt namiesto
 *    dvoch systémových promptov naraz. Koordinácia cez `window.__appConsentSettled`
 *    / event `app-consent-settled`, ktoré emituje CMP adaptér (viď runbook
 *    eshop-integration.md). Eshop bez CMP glue → subscribe hneď po APP_READY.
 *  - Manuálne: `window.AppPush.subscribe()` — pre opt-in tlačidlá alebo
 *    „prompt až po prvej objednávke" (per-projekt UX).
 *
 * Sink (kam ide token) — provider/framework-agnostický, poradie:
 *   1. `window.__appPushConfig.send(token, provider)` — vlastný API klient eshopu
 *      (odporúčané pri CSRF/interceptoroch, napr. axios util),
 *   2. `window.__appPushConfig.endpoint` — vstavaný fetch POST (credentials:
 *      include, CSRF z <meta name="csrfToken"> alebo cookie ak existuje),
 *   3. inak console.warn (token sa nikam nepošle).
 *
 * Vyžaduje načítaný app-bridge.js (`window.AppBridge`) — načítať PO ňom a PO
 * CMP adaptéri (nech je `window.__appConsentGluePresent` nastavený). Vite/TS
 * eshopy: `import "<cesta>/app-push.js"` (side-effect, žiadne exporty).
 */
(function () {
    "use strict";

    const bridge = window.AppBridge;

    // mimo appky no-op — stub API, nech projekt môže volať AppPush.subscribe()
    // bez guardovania existencie (rovnako ako window.AppBridge)
    if (!bridge || !bridge.isApp) {
        window.AppPush = {
            subscribe: function () {
                return Promise.resolve({});
            },
        };

        return;
    }

    const config = window.__appPushConfig || {};
    const sentTokens = new Set();

    const readCsrf = function () {
        const meta = document.querySelector('meta[name="csrfToken"]');

        if (meta && meta.getAttribute("content")) {
            return meta.getAttribute("content");
        }

        const cookie = document.cookie.split("; ").find(function (entry) {
            return entry.indexOf("csrfToken=") === 0;
        });

        return cookie ? decodeURIComponent(cookie.split("=")[1]) : "";
    };

    const warnSendFailed = function (token, error) {
        // uvoľni token, nech ho ďalší PUSH_TOKEN_READY (rotácia) skúsi znova
        sentTokens.delete(token);

        if (window.console) {
            console.warn("[app-push] odoslanie tokenu zlyhalo:", error);
        }
    };

    const sendToken = function (token, provider) {
        if (!token || sentTokens.has(token)) {
            return;
        }

        sentTokens.add(token);

        if (typeof config.send === "function") {
            try {
                Promise.resolve(config.send(token, provider)).catch(function (error) {
                    warnSendFailed(token, error);
                });
            } catch (error) {
                warnSendFailed(token, error);
            }

            return;
        }

        if (typeof config.endpoint === "string") {
            const headers = { "Content-Type": "application/json" };
            const csrf = readCsrf();

            if (csrf) {
                headers["X-CSRF-Token"] = csrf;
            }

            window
                .fetch(config.endpoint, {
                    method: "POST",
                    credentials: "include",
                    headers: headers,
                    body: JSON.stringify({ token: token, provider: provider }),
                })
                .catch(function (error) {
                    warnSendFailed(token, error);
                });

            return;
        }

        sentTokens.delete(token);

        if (window.console) {
            console.warn(
                "[app-push] žiadny sink (window.__appPushConfig.send / .endpoint) — token nezaslaný"
            );
        }
    };

    bridge.onBridgeEvent("PUSH_TOKEN_READY", function (payload) {
        if (payload) {
            sendToken(payload.token, payload.provider || "fcm");
        }
    });

    // OS prompt + FCM registrácia; token príde následne cez PUSH_TOKEN_READY.
    // Vracia promise s výsledkom {status} — projekt si môže ošetriť "denied".
    const subscribe = function () {
        return bridge.callBridge("PUSH_SUBSCRIBE");
    };

    window.AppPush = { subscribe: subscribe };

    if (config.autoPrompt === false) {
        return;
    }

    const autoSubscribe = function () {
        subscribe().catch(function (error) {
            if (window.console) {
                console.warn("[app-push] auto PUSH_SUBSCRIBE zlyhal:", error);
            }
        });
    };

    if (window.__appConsentSettled === true) {
        // consent už dorešený (napr. neskorý beh) — subscribe hneď
        autoSubscribe();
    } else if (window.__appConsentGluePresent === true) {
        // CMP adaptér oznámi dorešenie eventom; bez timeoutu — ATT prompt môže
        // trvať dlho. Ak by ATT sync zlyhal (rare), prompt sa nevyvolá — neškodí.
        const onSettled = function () {
            window.removeEventListener("app-consent-settled", onSettled);
            autoSubscribe();
        };

        window.addEventListener("app-consent-settled", onSettled);
    } else {
        // eshop bez CMP glue — nič nečakáme, subscribe po APP_READY
        bridge
            .whenAppReady()
            .then(autoSubscribe)
            .catch(function () {});
    }
})();
